import { DetailsRow, getTheme, IColumn, IDetailsListProps, IDetailsRowStyles, IImageProps } from '@fluentui/react';
import AppContext from 'App/AppContext';
import {
  IPImage,
  IPImageFit,
  IPLink,
  IPScrollablePane,
  IPScrollbarVisibility,
  IPSearchBox,
  IPSpinner,
  IPText,
} from 'baseComponents';
import { IPDetailsList, IPDetailsListLayoutMode } from 'baseComponents/DetailsList/IPDetailsList';
import { IPDropdown } from 'baseComponents/Dropdown/IPDropdown';
import { IPPersona, IPPersonaSize } from 'baseComponents/Persona/IPPersona';
import { IPSpinnerSize } from 'baseComponents/Spinner/IPSpinner';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { IPSelectableOption } from 'baseComponents/Utils/SelectableOption';
import { IPSelectionMode } from 'baseComponents/Utils/Selection';
import GenericModal from 'components/Dialogs/GenericModal';
import InfoMessage from 'components/Notification/InfoMessage';
import WarningMessage from 'components/Notification/WarningMessage';
import { globalFilterDelay } from 'globalConstants';
import { globalStackTokensGapMedium, globalStackTokensGapSmall } from 'globalStyles';
import { HomeAccount } from 'models/userSetting';
import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GraphScopeId } from 'services/Auth/authConfig';
import Config from 'services/Config/configService';
import { getGraphSharepointErrorMsg, toAppError } from 'services/Graph/graphErrors';
import { graphGetSites, graphGetSuggestedSites } from 'services/Graph/graphService';
import { getLocalStorageData, LocalStorageKeys, setLocalStorageData } from 'utils/localstorage';
import { ISite } from '../../services/Graph/SharepointInterfaces';

interface IMySiteProps {
  onSelect: (site: ISite) => void;
  selectedSite: ISite | undefined;
  checkToken: (token: string) => boolean;
  disallowHomeAccount?: boolean;
  authType: GraphScopeId;
  refresh?: boolean;
}

const MySites = (props: IMySiteProps) => {
  const appContext = useContext(AppContext);
  const { t } = useTranslation(['translation', 'sharepoint']);
  const theme = getTheme();
  const [sites, setSites] = useState<ISite[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState<string | undefined>(undefined);
  const [spError, setSPError] = useState<string | undefined>(undefined);
  const [selectedOrg, setSelectedOrg] = useState<string | undefined>(undefined);
  const [showSiteNotListedInfo, setShowSiteNotListedInfo] = useState<boolean>(false);

  const imagePropsSiteExample: IImageProps = {
    src: `${Config.getImageURL()}/ISOPlanner SharePoint Site Search URL Example.png`,
    imageFit: IPImageFit.center,
    width: 600,
    height: 200,
  };

  useEffect(() => {
    initOrgs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const initOrgs = () => {
    //setSelectedOrg is always called and this triggers the fetchSites function through the useEffect and timer
    //this is needed to ensure that fetchSites is called after the orgs are initialized and on mount
    const homeAccount = appContext.globalDataCache.userSettings.get(HomeAccount) as string;
    if (homeAccount && !props.disallowHomeAccount && appContext.user.tenant.providerTenantId) {
      if (appContext.user.login.isGuest) {
        const currentTenantId = getUserOrg(appContext.user.tenant.providerTenantId);
        setSelectedOrg(currentTenantId);
      }
    } else {
      setSelectedOrg(undefined);
    }
  };

  const getUserOrg = (defaultTenantId: string): string => {
    const val = getLocalStorageData(appContext, LocalStorageKeys.SharepointOrg) || defaultTenantId;

    return val.toString();
  };

  const fetchSites = async (searchText: string | undefined) => {
    try {
      if (isLoading) return;
      setIsLoading(true);
      setSPError(undefined);

      const graphInterface = await appContext.getGraphInterface(props.authType, selectedOrg);
      if (!props.checkToken(graphInterface.accessToken)) return;

      let _sites: ISite[];
      if (searchText !== undefined && searchText.trim().length > 0) {
        _sites = await graphGetSites(graphInterface.client, searchText);
      } else {
        _sites = await graphGetSuggestedSites(graphInterface.client, appContext);
      }

      if (_sites) {
        _sites.sort((a, b) => (a.displayName ? a.displayName.localeCompare(b.displayName ?? '') : 0));
      } else {
        _sites = [];
      }

      setSites(_sites);
    } catch (err) {
      setSites([]);
      const graphErrorMsg = getGraphSharepointErrorMsg(err, t);

      if (graphErrorMsg) {
        setSPError(graphErrorMsg);
      } else {
        appContext.setError(toAppError(err));
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => fetchSites(searchQuery), globalFilterDelay);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, selectedOrg, props.refresh]);

  const getColumns = (): IColumn[] => {
    return [
      {
        key: 'Sites',
        name: 'sites',
        minWidth: 150,
        maxWidth: 150,
        isRowHeader: true,
        isSorted: false,
        isSortedDescending: false,
        isMultiline: true,
        onRender: (_site: ISite, index: number | undefined) => (
          <IPStack horizontal verticalAlign="center" tokens={globalStackTokensGapSmall}>
            <IPStack.Item>
              <IPPersona
                text={_site.displayName || 'Unknown'}
                size={IPPersonaSize.size40}
                hidePersonaDetails={true}
                key={_site.id}
              />
            </IPStack.Item>
            <IPStack.Item>
              <IPText>{_site.displayName || 'Unknown'}</IPText>
            </IPStack.Item>
          </IPStack>
        ),
      },
    ];
  };

  const onRenderRow: IDetailsListProps['onRenderRow'] = (rowProps) => {
    let customStyles: Partial<IDetailsRowStyles> = {};

    if (rowProps?.item.id === props.selectedSite?.id) {
      customStyles = {
        root: {
          backgroundColor: theme.palette.themeSecondary,
          borderRadius: '3px',
          selectors: {
            '&:hover': {
              backgroundColor: theme.palette.themeSecondary,
            },
          },
        },
      };
    }

    if (rowProps) {
      return <DetailsRow {...rowProps} styles={customStyles} />;
    }

    return null;
  };

  const activeItemChanged = (
    item: ISite,
    index: number | undefined,
    ev: React.FocusEvent<HTMLElement> | undefined,
  ): void => {
    if (item.id !== props.selectedSite?.id) {
      item.tenantId = selectedOrg;
      props.onSelect(item);
    }
  };

  const getOrgs = (): IPSelectableOption[] => {
    if (!selectedOrg) return [];

    const homeAccount = appContext.globalDataCache.userSettings.get(HomeAccount) as string;
    if (!homeAccount) return [];
    const selectedOrgObj = appContext.user.login.tenants?.find((t) => t.providerTenantId === homeAccount);
    if (!selectedOrgObj || !selectedOrgObj.providerTenantId || !appContext.user.tenant.providerTenantId) return [];

    return [
      {
        key: appContext.user.tenant.providerTenantId,
        text: appContext.user.tenant.name,
      },
      {
        key: selectedOrgObj.providerTenantId,
        text: selectedOrgObj.name,
      },
    ];
  };

  //
  // Main render
  //
  return (
    <IPStack verticalFill tokens={globalStackTokensGapMedium} styles={{ root: { minHeight: 200 } }}>
      {selectedOrg && (
        <IPStack.Item>
          <IPDropdown
            selectedKey={selectedOrg}
            options={getOrgs()}
            styles={{ listbox: { maxHeight: 250 } }}
            onChange={(event: React.FormEvent<HTMLDivElement>, option?: IPSelectableOption, index?: number) => {
              if (!option) return;
              const tenantId = option.key as string;
              setLocalStorageData(appContext, LocalStorageKeys.SharepointOrg, tenantId);
              setSelectedOrg(tenantId);
            }}
          />
        </IPStack.Item>
      )}
      <IPStack.Item>
        <IPSearchBox
          className="isoplanner-sites-filter"
          underlined={false}
          placeholder={t('translation:General.Filter.Placeholder')}
          id="search"
          onChange={(ev, newValue) => {
            setSearchQuery(newValue);
          }}
        />
        <IPLink onClick={() => setShowSiteNotListedInfo(true)}>{t('sharepoint:Dialogs.SiteNotListed.Link')}</IPLink>
        <GenericModal
          title={t('sharepoint:Dialogs.SiteNotListed.Title')}
          isOpen={showSiteNotListedInfo}
          onClose={() => setShowSiteNotListedInfo(false)}
          minWidth={650}
          verticalGap={globalStackTokensGapSmall}
        >
          <IPText block>{t('sharepoint:Dialogs.SiteNotListed.SubText')}</IPText>
          <IPText>{t('sharepoint:Dialogs.SiteNotListed.SubSiteInfo')}</IPText>
          <IPImage {...imagePropsSiteExample} />
        </GenericModal>
      </IPStack.Item>
      <IPStack.Item grow styles={{ root: { position: 'relative' } }}>
        <IPScrollablePane scrollbarVisibility={IPScrollbarVisibility.auto}>
          {spError === undefined && !isLoading && sites.length === 0 && (
            <InfoMessage message={t('translation:General.Search.NoData')} onDismiss={undefined}></InfoMessage>
          )}
          {spError && <WarningMessage message={spError} onDismiss={undefined}></WarningMessage>}
          {isLoading && (
            <IPStack verticalFill horizontalAlign="center" verticalAlign="center">
              <IPSpinner size={IPSpinnerSize.large} />
            </IPStack>
          )}
          {!isLoading && (
            <IPDetailsList
              items={sites}
              columns={getColumns()}
              compact={true}
              layoutMode={IPDetailsListLayoutMode.justified}
              selectionMode={IPSelectionMode.none}
              selectionPreservedOnEmptyClick={true}
              isHeaderVisible={false}
              onActiveItemChanged={activeItemChanged}
              onRenderRow={onRenderRow}
            />
          )}
        </IPScrollablePane>
      </IPStack.Item>
    </IPStack>
  );
};

export default MySites;
